import type {
  AccountView,
  DebriefView,
  HistoryItem,
  MissionBrief,
  MissionSummary,
  OpenAccountResponse,
  TerminalSnapshot
} from '@head-of-trading/contracts';

export interface MissionCatalogItem extends MissionSummary {
  readonly brief?: MissionBrief;
}

export interface CreateAttemptResult {
  readonly attemptId: string;
  readonly snapshot?: TerminalSnapshot;
}

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

const asRecord = (value: unknown): Record<string, unknown> | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : null;
const baseUrl = import.meta.env.BASE_URL === '/' ? '' : import.meta.env.BASE_URL.replace(/\/$/, '');
const endpoint = (path: string): string => `${baseUrl}${path}`;

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(endpoint(path), {
    credentials: 'same-origin',
    ...init,
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', ...(init?.headers ?? {}) }
  });
  const text = await response.text();
  let body: unknown = null;
  if (text) {
    try { body = JSON.parse(text) as unknown; } catch { body = text; }
  }
  if (!response.ok) {
    const record = asRecord(body);
    const message = typeof record?.message === 'string' ? record.message : `La requête a échoué (${response.status}).`;
    throw new ApiError(response.status, message, body);
  }
  const record = asRecord(body);
  return (record && 'data' in record ? record.data : body) as T;
}

function unwrap<T>(value: unknown, keys: readonly string[]): T {
  const record = asRecord(value);
  for (const key of keys) if (record && key in record) return record[key] as T;
  return value as T;
}

export const api = {
  async openAccount(pseudo: string): Promise<OpenAccountResponse> {
    const value = await request<unknown>('/api/accounts/open', { method: 'POST', body: JSON.stringify({ pseudo }) });
    const record = asRecord(value);
    if (record && 'account' in record) return value as OpenAccountResponse;
    return { account: value as AccountView, takeoverRequired: false };
  },
  async takeover(accountId: string): Promise<AccountView> {
    const value = await request<unknown>(`/api/accounts/${encodeURIComponent(accountId)}/takeover`, { method: 'POST', body: '{}' });
    return unwrap<AccountView>(value, ['account']);
  },
  async missions(): Promise<readonly MissionCatalogItem[]> {
    const value = await request<unknown>('/api/missions');
    return unwrap<readonly MissionCatalogItem[]>(value, ['missions', 'items']);
  },
  async createAttempt(missionId: string): Promise<CreateAttemptResult> {
    const value = await request<unknown>('/api/attempts', { method: 'POST', body: JSON.stringify({ missionId }) });
    const record = asRecord(value);
    const nested = asRecord(record?.attempt);
    const snapshot = (record?.snapshot ?? nested?.snapshot) as TerminalSnapshot | undefined;
    const attemptId = String(record?.attemptId ?? nested?.attemptId ?? nested?.id ?? snapshot?.attemptId ?? '');
    if (!attemptId) throw new ApiError(500, 'Le serveur n’a pas retourné l’identifiant de la tentative.', value);
    return snapshot ? { attemptId, snapshot } : { attemptId };
  },
  async attempt(attemptId: string): Promise<TerminalSnapshot> {
    const value = await request<unknown>(`/api/attempts/${encodeURIComponent(attemptId)}`);
    return unwrap<TerminalSnapshot>(value, ['snapshot', 'attempt']);
  },
  async abandon(attemptId: string): Promise<void> {
    await request<unknown>(`/api/attempts/${encodeURIComponent(attemptId)}/abandon`, { method: 'POST', body: '{}' });
  },
  async history(accountId: string): Promise<readonly HistoryItem[]> {
    const value = await request<unknown>(`/api/accounts/${encodeURIComponent(accountId)}/history`);
    return unwrap<readonly HistoryItem[]>(value, ['history', 'items']);
  },
  async debrief(attemptId: string): Promise<DebriefView> {
    const value = await request<unknown>(`/api/attempts/${encodeURIComponent(attemptId)}/debrief`);
    return unwrap<DebriefView>(value, ['debrief']);
  }
};
